import axios from "axios";
import {tokenStorage} from "../utils/tokenStorage";
import {CognitoRefreshToken, CognitoUser, CognitoUserPool} from "amazon-cognito-identity-js";
import { PublicClientApplication } from "@azure/msal-browser";
import {user_pool_id, client_id, API_URL, TEST_API_URL, entra_id_client_id, entra_id_tenant_id, IS_PRODUCTION} from "../utils/url_constants";

// Environment Configuration
const BASE_URL = IS_PRODUCTION ? API_URL : TEST_API_URL;

const poolData = {
    UserPoolId: user_pool_id,
    ClientId: client_id
};
const userPool = new CognitoUserPool(poolData);

// Active Directory (Entra ID) MSAL Setup
const msalConfig = {
    auth: {
        clientId: entra_id_client_id,
        authority: `https://login.microsoftonline.com/${entra_id_tenant_id || "common"}`,
    },
    cache: { cacheLocation: "sessionStorage" }
};
export const msalInstance = new PublicClientApplication(msalConfig);

// Initialize MSAL early (required for MSAL v3+)
msalInstance.initialize().catch(console.error);

// Create an axios instance
const axiosInstance = axios.create({
    baseURL: BASE_URL,
});


// Flag to prevent multiple token refreshes in parallel
let isRefreshing = false;
let failedQueue = [];

// Process queued requests after token refresh
const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });

    failedQueue = [];
};

/**
 * Refreshes tokens for AWS Cognito users
 */
const refreshCognitoTokens = () => {
    return new Promise((resolve, reject) => {
        const refreshToken = tokenStorage.getRefreshToken();
        if (!refreshToken) return reject(new Error('No Cognito refresh token available'));

        const cognitoUser = new CognitoUser({
            Username: localStorage.getItem('lastUsername') || '',
            Pool: userPool
        });

        const refreshCognitoToken = new CognitoRefreshToken({ RefreshToken: refreshToken });

        cognitoUser.refreshSession(refreshCognitoToken, (err, session) => {
            if (err) {
                console.error('Cognito token refresh failed:', err);
                return reject(err);
            }

            const expiresInSeconds = session.getIdToken().getExpiration() - Math.floor(Date.now() / 1000);

            tokenStorage.setTokens(
                session.getIdToken().getJwtToken(),
                session.getAccessToken().getJwtToken(),
                session.getRefreshToken().getToken(),
                expiresInSeconds,
                'cognito'
            );

            resolve();
        });
    });
};

/**
 * Refreshes tokens silently for Azure AD / Entra ID users via MSAL
 */
const refreshEntraTokens = async () => {
    const activeAccount = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
    if (!activeAccount) throw new Error('No active Azure AD account session found.');

    const response = await msalInstance.acquireTokenSilent({
        scopes: ["openid", "profile", "email"],
        account: activeAccount
    });

    const expiresInSeconds = Math.floor((response.expiresOn.getTime() - Date.now()) / 1000);

    tokenStorage.setTokens(
        response.idToken,
        response.accessToken,
        null, // MSAL manages refresh token cache internally
        expiresInSeconds,
        'entra'
    );
};

/**
 * Master refresh handler that branches according to current active provider
 */
const refreshSessionTokens = async () => {
    const provider = tokenStorage.getAuthProvider();
    if (provider === 'entra') {
        await refreshEntraTokens();
    } else {
        await refreshCognitoTokens();
    }
};

// Request Interceptor
axiosInstance.interceptors.request.use(
    async (config) => {
        const useIdToken = config.url?.includes('execute-api') || config.useSpecialAPI;

        if (tokenStorage.isTokenExpired()) {
            if (!isRefreshing) {
                isRefreshing = true;

                try {
                    await refreshSessionTokens();
                    const activeToken = useIdToken ? tokenStorage.getIdToken() : tokenStorage.getAccessToken();
                    processQueue(null, activeToken);
                } catch (error) {
                    console.error('Session restoration failed:', error);
                    processQueue(error, null);
                    tokenStorage.clearTokens();
                    window.location.href = '/login';
                    return Promise.reject(error);
                } finally {
                    isRefreshing = false;
                }
            } else {
                // Queue concurrent API requests while token refresh executes
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject, useIdToken });
                })
                    .then(token => {
                        config.headers['Authorization'] = `Bearer ${token}`;
                        return config;
                    })
                    .catch(error => Promise.reject(error));
            }
        }

        // Attach Authorization Header
        const token = useIdToken ? tokenStorage.getIdToken() : tokenStorage.getAccessToken();
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }

        return config;
    },
    (error) => Promise.reject(error)
);

// Response Interceptor
axiosInstance.interceptors.response.use(
    response => response,
    error => {
        console.error('API Error:', error);
        if (error.response && error.response.status === 401) {
            tokenStorage.clearTokens();
            // window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

class ApiClient{

    async get_user_access(user_email){
        let response = await axiosInstance({
            method: "POST",
            url: "getuseraccess",
            data: {user_email:user_email},
        });
        return response.data;
    }

    async get_blob_post_url(file_name){
        try {
            let response = await axiosInstance({
                method: "POST",
                url: "get-upload-url",
                data: {file_url: file_name}
            });
            return response.data;
        } catch (err) {
            console.error('get_blob_post_url status:', err.response?.status);
            console.error('get_blob_post_url data:', err.response?.data);
            console.error('get_blob_post_url full error:', err);
            throw err;
        }
    }

    async insert_doc_mapping(payload) {
        try {
            let response = await axiosInstance({
                method: "POST",
                url: "insert-doc-mapping",
                data: payload
            });
            return response.data;
        } catch (err) {
            console.error('insert_doc_mapping error status:', err.response?.status);
            console.error('insert_doc_mapping error data:', err.response?.data);
            throw err;
        }
    }

    async vertexAI(payload) {
        try {
            let response = await axiosInstance({
                method: "POST",
                url: "vertexai",
                data: payload
            });
            return response.data;
        } catch (err) {
            console.error('vertexAI error status:', err.response?.status);
            console.error('vertexAI error data:', err.response?.data);
            throw err;
        }
    }

    async get_unread_count(user_email){
        let response = await axiosInstance({
            method: "POST",
            url: "getunreadcount",
            data: {user_email:user_email},
        });
        return response.data;
    }

    async get_mycases_unread(user_email){
        let response = await axiosInstance({
            method: "POST",
            url: "getunreadmycases",
            data: {user_email:user_email},
        });
        return response.data;
    }

    async getaiassessment(stack_id){
        let response = await axiosInstance({
            method: "POST",
            url: "getaiassessment",
            data: {stack_id:stack_id},
        });
        return response.data;
    }

    async markasreadpro(stack_id, user_email){
        let response = await axiosInstance({
            method: "POST",
            url: "markasreadpro",
            data: {stack_id:stack_id, user_email:user_email},
        });
        return response.data;
    }

    async get_all_cases_ai(start_date,end_date, status, roletype, license, requester_license){
        let response = await axiosInstance({
            method: "POST",
            url: "getallcasesai",
            data: {start_date:start_date, end_date:end_date, status:status, roletype:roletype, license:license, requester_license:requester_license},
        });
        return response.data;
    }

    async get_preview_url(file_url){
        let response = await axiosInstance({
            method: "POST",
            url: "get-preview-url",
            data: {file_url:file_url},
        });
        return response.data;
    }

    async get_insurers(){
        let response = await axiosInstance({
            method: "POST",
            url: "get-insurers",
            data: {},
        });
        return response.data;
    }

    async update_policy_prompt(license, policynumber, prompt){
        let response = await axiosInstance({
            method: "POST",
            url: "update-policy-prompt",
            data: {license:license, policynumber:policynumber, prompt:prompt},
        });
        return response.data;
    }

    async get_policy_prompt(license, policynumber){
        let response = await axiosInstance({
            method: "POST",
            url: "get-policy-prompt",
            data: {license:license, policynumber:policynumber},
        });
        return response.data;
    }

    async update_soc_prompt(active,license, prompt){
        let response = await axiosInstance({
            method: "POST",
            url: "update-soc-prompt",
            data: {license:license, soc_prompt:prompt, active:active},
        });
        return response.data;
    }

    async get_soc_prompt(license){
        let response = await axiosInstance({
            method: "POST",
            url: "get-soc-prompt",
            data: {license:license},
        });
        return response.data;
    }

    async getpolicynumberbylicense(license_number){
        let response = await axiosInstance({
            method: "POST",
            url: "getpolicynumberbylicense",
            data: {license_number:license_number},
        });
        return response.data;
    }

    /**
     * ROLE MANAGEMENT
     */

    async getallroles(license_number){
        let response = await axiosInstance({
            method: "POST",
            url: "getallroles",
            data: {license:license_number},
        });
        return response.data;
    }

    async updaterole(role_id, active){
        let response = await axiosInstance({
            method: "POST",
            url: "updaterole",
            data: {role_id:role_id,active:active},
        });
        return response.data;
    }

    async addrole(payload){
        let response = await axiosInstance({
            method: "POST",
            url: "addrole",
            data: payload
        });
        return response.data;
    }

    async set_auth_register(email, password){
        let response = await axiosInstance({
            method: "POST",
            url: "auth/register",
            data: {email:email, password:password}
        });
        return response.data;
    }

    /**
     * ADMIN MANAGEMENT
     */

    // LICENSE MANAGEMENT

    async adminlistlicenses(){
        let response = await axiosInstance({
            method: "POST",
            url: "adminlistlicenses",
            data: {},
        });
        return response.data;
    }

    async adminaddlicense(license, company, issued, expires, email, roletype){
        let response = await axiosInstance({
            method: "POST",
            url: "adminaddlicense",
            data: {license_text:license, company:company, issued:issued, expires:expires, email:email, roletype:roletype},
        });
        return response.data;
    }

    async adminupdatelicense(license, issued, expires, email, roletype, active){
        let response = await axiosInstance({
            method: "POST",
            url: "adminupdatelicense",
            data: {license_text:license, issued:issued, expires:expires, email:email, roletype:roletype,active:active},
        });
        return response.data;
    }

    async adminremovelicense(license){
        let response = await axiosInstance({
            method: "POST",
            url: "adminremovelicense",
            data: {license_text:license},
        });
        return response.data;
    }

    // POLICIES MANAGEMENT
    async getpoliciesbylicense(license){
        let response = await axiosInstance({
            method: "POST",
            url: "getpoliciesbylicense",
            data: {license:license},
        });
        return response.data;
    }

    async createpolicynumber(policynumber, license){
        let response = await axiosInstance({
            method: "POST",
            url: "createpolicynumber",
            data: {policynumber:policynumber, license:license}
        });
        return response.data;
    }

    async deletepolicynumber(policynumber, license){
        let response = await axiosInstance({
            method: "POST",
            url: "deletepolicynumber",
            data: {policynumber:policynumber, license:license}
        });
        return response.data;
    }

    async get_counter(requester_license){
        let response = await axiosInstance({
            method: "POST",
            url: "getcounter",
            data: {requester_license:requester_license}
        });
        return response.data;
    }

    /**
     * DOCTORS MANAGEMENT
     * Talks to DoctorsList_Router.js (GET/POST/PUT/DELETE /api/doctors...)
     */

    async get_all_doctors(){
        try {
            let response = await axiosInstance({
                method: "GET",
                url: "doctors",
            });
            return response.data;
        } catch (err) {
            console.error('get_all_doctors error:', err.response?.data || err);
            throw err;
        }
    }

    async get_doctor_by_id(id){
        try {
            let response = await axiosInstance({
                method: "GET",
                url: `doctors/${id}`,
            });
            return response.data;
        } catch (err) {
            console.error('get_doctor_by_id error:', err.response?.data || err);
            throw err;
        }
    }

    async search_doctors(query, options = {}){
        try {
            let response = await axiosInstance({
                method: "GET",
                url: "doctors/search",
                params: {query:query, ...options},
            });
            return response.data;
        } catch (err) {
            console.error('search_doctors error:', err.response?.data || err);
            throw err;
        }
    }

    async add_doctor(name, specialty){
        try {
            let response = await axiosInstance({
                method: "POST",
                url: "doctors",
                data: {name:name, specialty:specialty},
            });
            return response.data;
        } catch (err) {
            console.error('add_doctor error:', err.response?.data || err);
            throw err;
        }
    }

    async update_doctor(id, name, specialty){
        try {
            let response = await axiosInstance({
                method: "PUT",
                url: `doctors/${id}`,
                data: {name:name, specialty:specialty},
            });
            return response.data;
        } catch (err) {
            console.error('update_doctor error:', err.response?.data || err);
            throw err;
        }
    }

    async remove_doctor(id){
        try {
            let response = await axiosInstance({
                method: "DELETE",
                url: `doctors/${id}`,
            });
            return response.data;
        } catch (err) {
            console.error('remove_doctor error:', err.response?.data || err);
            throw err;
        }
    }

    async refresh_doctors_cache(){
        try {
            let response = await axiosInstance({
                method: "POST",
                url: "doctors/refresh-cache",
            });
            return response.data;
        } catch (err) {
            console.error('refresh_doctors_cache error:', err.response?.data || err);
            throw err;
        }
    }
    //END CLASS API CLIENT
}

export default ApiClient;